interface SelectProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

interface AxisSelectProps {
  value: number | string | undefined;
  onChange: (value: number | string | undefined) => void;
}

// SPH球镜度选择器 (+600 to -2000)
export function DiopterSelect({ value, onChange }: SelectProps) {
  const options = [
    '+600', '+575', '+550', '+525', '+500', '+475', '+450', '+425', '+400',
    '+375', '+350', '+325', '+300', '+275', '+250', '+225', '+200', '+175',
    '+150', '+125', '+100', '+75', '+50', '+25', '-', '0',
    '-25', '-50', '-75', '-100', '-125', '-150', '-175', '-200', '-225',
    '-250', '-275', '-300', '-325', '-350', '-375', '-400', '-425', '-450',
    '-475', '-500', '-525', '-550', '-575', '-600', '-625', '-650', '-675',
    '-700', '-725', '-750', '-775', '-800', '-825', '-850', '-875', '-900',
    '-925', '-950', '-975', '-1000', '-1050', '-1100', '-1150', '-1200',
    '-1250', '-1300', '-1350', '-1400', '-1450', '-1500', '-1550', '-1600',
    '-1650', '-1700', '-1750', '-1800', '-1850', '-1900', '-1950', '-2000'
  ];

  return (
    <select
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      value={value !== undefined && value !== null ? String(value) : '-'}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' || v === '-' ? undefined : parseInt(v, 10));
      }}
    >
      {options.map((opt) => {
        const val = opt.replace('+', '');
        return (
          <option key={opt} value={val}>
            {opt}
          </option>
        );
      })}
    </select>
  );
}

// CYL柱镜度选择器 (+400 to -800)
export function DiopterCylinderSelect({ value, onChange }: SelectProps) {
  const options = [
    '+400', '+375', '+350', '+325', '+300', '+275', '+250', '+225', '+200',
    '+175', '+150', '+125', '+100', '+75', '+50', '+25', '-', '0',
    '-25', '-50', '-75', '-100', '-125', '-150', '-175', '-200', '-225',
    '-250', '-275', '-300', '-325', '-350', '-375', '-400', '-425', '-450',
    '-475', '-500', '-525', '-550', '-575', '-600', '-625', '-650', '-675',
    '-700', '-725', '-750', '-775', '-800'
  ];

  return (
    <select
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      value={value !== undefined && value !== null ? String(value) : '-'}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' || v === '-' ? undefined : parseInt(v, 10));
      }}
    >
      {options.map((opt) => {
        const val = opt.replace('+', '');
        return (
          <option key={opt} value={val}>
            {opt}
          </option>
        );
      })}
    </select>
  );
}

// Axis轴位选择器 (0-180)
export function AxisSelect({ value, onChange }: AxisSelectProps) {
  // 使用零宽字符区分第二段 0..180，避免 select 选择相同值时自动滚到第一项
  const HIDDEN = '\u200B';
  const baseOptions = Array.from({ length: 181 }, (_, i) => i); // 0..180
  const firstBlock = baseOptions.map((n) => ({ label: String(n), val: String(n) }));
  const secondBlock = baseOptions.map((n) => ({ label: String(n), val: String(n) + HIDDEN }));
  const options = [...firstBlock, { label: '-', val: '-' }, ...secondBlock];

  return (
    <select
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      value={value !== undefined && value !== null ? String(value) : '-'}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '' || raw === '-') {
          onChange(undefined);
          return;
        }
        // 若是第二段，保留隐藏字符存入上层状态；否则存数字
        if (raw.endsWith(HIDDEN)) {
          onChange(raw);
        } else {
          onChange(parseInt(raw, 10));
        }
      }}
    >
      {options.map((opt, idx) => (
        <option key={`${opt.val}-${idx}`} value={opt.val}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// PD瞳距选择器 (50-80)
export function PDSelect({ value, onChange }: SelectProps) {
  // 50 - 80，间隔 0.5；在 59 和 60 之间插入 '-'
  const options: (number | string)[] = [];
  for (let i = 0; i <= 60; i++) {
    const v = 50 + i * 0.5;
    const display = Number(v.toFixed(1));
    options.push(display);
    if (display === 59) {
      options.push('-');
    }
  }

  return (
    <select
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      value={value !== undefined && value !== null ? String(value) : '-'}
      onChange={(e) => onChange(e.target.value === '' || e.target.value === '-' ? undefined : parseFloat(e.target.value))}
    >
      {options.map((opt, idx) => (
        <option key={`${opt}-${idx}`} value={String(opt)}>
          {opt}
        </option>
      ))}
    </select>
  );
}

// PH瞳高选择器 (5-50，0.5步长，19.5 与 20 之间插入 '-')
export function PHSelect({ value, onChange }: SelectProps) {
  const options: (number | string)[] = [];
  for (let i = 0; i <= 90; i++) {
    const v = 5 + i * 0.5;
    const display = Number(v.toFixed(1));
    options.push(display);
    if (display === 19.5) {
      options.push('-');
    }
  }

  return (
    <select
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      value={value !== undefined && value !== null ? String(value) : '-'}
      onChange={(e) => onChange(e.target.value === '' || e.target.value === '-' ? undefined : parseFloat(e.target.value))}
    >
      {options.map((opt, idx) => (
        <option key={`${opt}-${idx}`} value={String(opt)}>
          {opt}
        </option>
      ))}
    </select>
  );
}

// ADD下加光选择器 (50-350，25步长，75 与 100 之间插入 '-')
export function AddSelect({ value, onChange }: SelectProps) {
  const options: (number | string)[] = ['-'];
  for (let v = 50; v <= 350; v += 25) {
    options.push(v);
  }

  return (
    <select
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      value={value !== undefined && value !== null ? String(value) : '-'}
      onChange={(e) => onChange(e.target.value === '' || e.target.value === '-' ? undefined : parseInt(e.target.value, 10))}
    >
      {options.map((opt, idx) => (
        <option key={`${opt}-${idx}`} value={String(opt)}>
          {opt}
        </option>
      ))}
    </select>
  );
}

